import type { LocationQuery, LocationQueryRaw } from "vue-router";

import { backendOld } from "~/api";
import {
  MODEL_TO_DISPLAY_NAME,
  MODEL_TO_ICON,
  MODEL_TO_SUBTITLE,
  ORDERED_PROVIDERS,
  PROVIDER_TO_MODELS_ORDERED,
} from "~/constants/model";
import { ArrowUpRightIcon, ModelsIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { AiModel, DropdownMenuItemKind, Subscription, UserRole } from "~/shared/enums";
import type { DropdownMenuSection } from "~/shared/types";
import { useAppStore, useDataStore, useTenantStore, useUserStore } from "~/stores";

export const CHAT_DUID_QUERY_PARAM = "chat";

export const getChatDuidFromQuery = (query: LocationQuery) => {
  const chatDuid = query[CHAT_DUID_QUERY_PARAM];
  const chatDuidString = Array.isArray(chatDuid) ? chatDuid[0] : chatDuid;
  return chatDuidString?.substring(0, 12);
};

export const makeQueryWithoutChatDuid = (query: LocationQuery): LocationQueryRaw => ({
  ...query,
  [CHAT_DUID_QUERY_PARAM]: undefined,
});

const buildAiModelDropdownSections = ({
  currentModel,
  onModelSelect,
  models,
  allowAllProviders,
}: {
  currentModel: AiModel;
  onModelSelect: (model: AiModel) => void;
  models?: AiModel[];
  allowAllProviders?: boolean;
}): DropdownMenuSection[] => {
  const tenantStore = useTenantStore();
  const userStore = useUserStore();

  const modelSet = models ? new Set(models) : null;
  const enabledProviders = new Set(allowAllProviders ? ORDERED_PROVIDERS : tenantStore.enabledAiProviders);
  const createModelItems = (providerModels: AiModel[]) =>
    providerModels
      .filter((model) => !modelSet || modelSet.has(model))
      .map((model) => ({
        title: MODEL_TO_DISPLAY_NAME.get(model) ?? "",
        subtitle: MODEL_TO_SUBTITLE.get(model) ?? "",
        kind: DropdownMenuItemKind.BUTTON,
        disabled: currentModel === model,
        icon: MODEL_TO_ICON.get(model),
        onClick: () => onModelSelect(model),
      }));

  return [
    {
      title: "Auto",
      items:
        !modelSet || modelSet.has(AiModel.AUTO)
          ? [
              {
                title: MODEL_TO_DISPLAY_NAME.get(AiModel.AUTO) ?? "",
                subtitle: MODEL_TO_SUBTITLE.get(AiModel.AUTO) ?? "",
                kind: DropdownMenuItemKind.BUTTON,
                icon: MODEL_TO_ICON.get(AiModel.AUTO),
                iconArgs: { class: "rounded icon-sm" },
                disabled: currentModel === AiModel.AUTO,
                onClick: () => onModelSelect(AiModel.AUTO),
              },
            ]
          : [],
    },
    {
      title: "Models",
      items: ORDERED_PROVIDERS.filter(
        (provider) => enabledProviders.has(provider) || PROVIDER_TO_MODELS_ORDERED[provider].includes(currentModel)
      ).flatMap((provider) => createModelItems(PROVIDER_TO_MODELS_ORDERED[provider])),
    },
    {
      title: "Add models",
      items:
        userStore.isRoleGte(UserRole.ADMIN) && ORDERED_PROVIDERS.some((provider) => !enabledProviders.has(provider))
          ? [
              {
                title: "Set up more models",
                kind: DropdownMenuItemKind.INTERNAL_LINK,
                icon: ModelsIcon,
                iconAfter: ArrowUpRightIcon,
                iconAfterArgs: { class: "text-lt icon-sm" },
                navigate: { to: makeLinkToSettingsRef("models").value },
              },
            ]
          : [],
    },
  ].filter((section) => section.items.length > 0);
};

export const openChatPanel = () => {
  const appStore = useAppStore();
  const tenantStore = useTenantStore();
  const userStore = useUserStore();

  if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
    appStore.setUpgradeConfirmationModalOpen({
      featureAction: "chat with Dart AI",
      subscription: Subscription.PREMIUM,
    });
    return;
  }
  if (userStore.getAiChatPanelOpen()) {
    appStore.chatPanel?.createNewChat();
    return;
  }
  userStore.setAiChatPanelOpen(true);
};

export const startDemoChat = async () => {
  const appStore = useAppStore();
  const dataStore = useDataStore();
  const tenantStore = useTenantStore();

  if (!tenantStore.isSubscriptionGte(Subscription.APP_SUMO)) {
    appStore.setUpgradeConfirmationModalOpen({
      featureAction: "chat with Dart AI",
      subscription: Subscription.PREMIUM,
    });
    return undefined;
  }

  const { data } = await backendOld.chat.startDemoChat();
  dataStore.$createChat(data.chat);
  return data.chat;
};

export default buildAiModelDropdownSections;
